import { Avatar, DropdownMenu, DropdownMenuSize, LogOutIcon } from "@landtechnologies/components";
import { BillingWrapper } from "react-migration/domains/billing";
import { useTranslation } from "react-migration/lib";
import { ENVIRONMENT } from "src/js/util/environment";
import { FullNameRow } from "./components";
import { SupportRow } from "./components/SupportRow";
import { createPortal } from "react-dom";

export type UserDropdownMenuProps = {
  initials: string;
  fullName: string;
  plan: string;
  align?: keyof (typeof DropdownMenu)["TriggerAlignment"];
  onSupportClick: () => void;
  stripeManagedSignup: boolean;
  usAccess: boolean;
};

const US_CONTACT_EMAIL = "calum@land.tech";
const UK_PHONE = "0203 086 7855";

export const UserDropdownMenu = ({
  initials,
  fullName,
  plan,
  align = "BOTTOM",
  stripeManagedSignup,
  onSupportClick,
  usAccess,
}: UserDropdownMenuProps) => {
  const { t } = useTranslation();

  const LOGOUT_URL = ENVIRONMENT.LOGOUT_URL;
  const fullNameClasses = "atlas-text-sm atlas-text-content-secondary atlas-font-semibold ";

  return (
    <DropdownMenu.Provider size={DropdownMenuSize.AUTO}>
      <DropdownMenu.Trigger
        data-testid="usermenu-trigger-element"
        align={DropdownMenu.TriggerAlignment[align]}
      >
        <Avatar initials={initials} className="atlas-cursor-pointer atlas-mr-2" />
      </DropdownMenu.Trigger>
      {createPortal(
        <DropdownMenu.Content className="atlas-min-w-[242px]">
          <DropdownMenu.ItemLink className={fullNameClasses}>
            <BillingWrapper>
              <FullNameRow fullName={fullName} />
            </BillingWrapper>
          </DropdownMenu.ItemLink>

          <DropdownMenu.Separator />
          <DropdownMenu.ItemText className="atlas-flex atlas-items-center atlas-gap-1">
            <span className="atlas-text-lg atlas-font-semibold">
              {plan.slice(0, 1).toUpperCase() + plan.slice(1, plan.length)} Plan
            </span>
          </DropdownMenu.ItemText>
          <DropdownMenu.Separator />
          <DropdownMenu.ItemText>
            {usAccess ? (
              <SupportRow onSupportClick={onSupportClick} contactEmail={US_CONTACT_EMAIL} />
            ) : (
              <SupportRow onSupportClick={onSupportClick} contactPhone={UK_PHONE} />
            )}
          </DropdownMenu.ItemText>
          <DropdownMenu.Separator />
          <DropdownMenu.ItemLink
            href={LOGOUT_URL}
            data-testid="logout-element"
            className="atlas-flex atlas-items-center atlas-gap-2"
          >
            <LogOutIcon />
            <span>{t("nav.nav.logout")}</span>
          </DropdownMenu.ItemLink>
        </DropdownMenu.Content>,
        document.body
      )}
    </DropdownMenu.Provider>
  );
};

UserDropdownMenu.displayName = "UserDropdownMenu";
